import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import { downloadBlob } from "@/apis/config/http";
import { STUDY_DOWNLOAD_BRIDGE_MODULE_ID } from "@/features/installations/constants/installationDefaults";
import { requestInstallationPackage } from "@/features/installations/services/installationPackageService";
import type { User } from "@/features/users/schemas/user";
import { getUserFullName } from "@/features/users/utils/user-utils";

export type ModuleId =
  | "pacs"
  | "router"
  | "visor"
  | "study-download-center-bridge"
  | "his"
  | "ris";

export interface ModuleDef {
  id: ModuleId;
  name: string;
  description: string;
}

export const MODULES: ModuleDef[] = [
  {
    id: "pacs",
    name: "PACS",
    description: "Almacenamiento DICOM (dcm4chee) e interfaz PACS",
  },
  {
    id: "router",
    name: "Router",
    description: "Router DICOM 2.0 (C-STORE, envío a cloud y licencia)",
  },
  {
    id: "visor",
    name: "Visor",
    description: "Visor OHIF y proxy de estudios",
  },
  {
    id: STUDY_DOWNLOAD_BRIDGE_MODULE_ID,
    name: "Study Download Bridge",
    description:
      "Descarga estudios cloud → PACS local (BRIDGE_API_KEY + INSTITUTION_ID en .env)",
  },
  {
    id: "his",
    name: "HIS",
    description: "Sistema de Información Hospitalaria",
  },
  {
    id: "ris",
    name: "RIS",
    description: "Sistema de Información de Radiología",
  },
];

export interface UsePreconfiguredDownloadArgs {
  user: User;
  /** UID del centro (workspace en Storage del lado servidor). */
  storageOwnerUid: string;
  modules: ModuleId[];
  caddyDomain: string;
  prefixDicom: string;
  /**
   * Si se informa, se envía en el JSON para trazabilidad/validación de enlace en servidor.
   */
  installLinkId?: string;
  /** Referencia a una API Key (existente o recién creada). */
  apiKeyId?: string;
}

function defaultZipFilename(userName: string): string {
  const slug = userName.replace(/\s+/g, "-").toLowerCase() || "instalacion";
  return `instalaciones-${slug}.zip`;
}

export function usePreconfiguredDownload() {
  return useMutation({
    mutationFn: async ({
      user,
      storageOwnerUid,
      modules,
      caddyDomain,
      prefixDicom,
      installLinkId,
      apiKeyId,
    }: UsePreconfiguredDownloadArgs) => {
      if (modules.length === 0) {
        throw new Error("Selecciona al menos un módulo");
      }
      if (!caddyDomain?.trim()) {
        throw new Error("CADDY_DOMAIN es obligatorio");
      }
      if (!storageOwnerUid?.trim()) {
        throw new Error("No se pudo determinar la institución");
      }

      const ownerLabel = getUserFullName(user) || storageOwnerUid;
      const { blob, filename } = await requestInstallationPackage(
        {
          institutionUid: storageOwnerUid.trim(),
          modules,
          caddyDomain: caddyDomain.trim(),
          institutionName: ownerLabel,
          ...(prefixDicom.trim() ? { prefixDicom: prefixDicom.trim() } : {}),
          ...(installLinkId?.trim() ? { installLinkId: installLinkId.trim() } : {}),
          ...(apiKeyId?.trim() ? { apiKeyId: apiKeyId.trim() } : {}),
        }
      );

      const name = filename ?? defaultZipFilename(ownerLabel || "centro");
      downloadBlob(blob, name);

      return { success: true as const, linkId: installLinkId };
    },
    onSuccess: () => {
      toast.success("Descarga iniciada", {
        description:
          "El archivo ZIP generado por el servidor se está descargando.",
      });
    },
    onError: (error: Error) => {
      toast.error("Error al descargar", {
        description: error.message || "No se pudo generar la descarga.",
      });
    },
  });
}
